import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { Hall } from '@weblite/ts-action/hall'
import { HallAdvancedSettingsOptions } from '$comp/_app/components/leftPanel/components/define-hall-advanced-settings'
import {
  PremiumFeatureType,
  showBuyPremiumFeatureDialog,
} from '$comp/modals/buy-premium-feature-dialog'
import {
  COURSE_VITRINE_TAG,
  DISABLE_WATERMARK_TAG,
  HIDE_MEMBER_COUNT_TAG,
  ISSUE_TAG,
  VOD_ACTIVATED_TAG,
} from '$logic/hall/constant'
import { addTagToHallTags, removeTagFromHallTags } from '$logic/hall/helper'
import {
  useIsOfflineCoursePlanEnabled,
  useIsPremiumPlanEnabled,
} from '$logic/premium-features/react-hooks'
import { useUserSessionsLimit } from '$logic/hall/react-hook'
import { openUserSessionsLimit } from '$logic/router'
import { isCallStarted } from '$logic/call'
import { showChangeWatermarkSettingsConfirmationDialog } from '$comp/modals/confirmation-dialog'
import { useRoom } from '$logic/room/react-hook'

export interface HallAdvancedSettingsRefApi {
  getCourseVitrine: () => boolean
  getIssue: () => boolean
  getProtectContent: () => boolean
  getTags: () => string[]
  getVodActivated: () => boolean
  getHideMemberCount: () => boolean
  getWatermarkDisabled: () => boolean
}

export const EditHallAdvancedSettings = forwardRef(
  function HallAdvancedSettings(
    { hall }: { hall: Hall },
    ref: ForwardedRef<HallAdvancedSettingsRefApi>,
  ) {
    const [tags, setTags] = useState<string[]>(hall.tags || [])

    const room = useRoom(hall.roomId)

    useEffect(() => {
      setTags(hall.tags ?? [])
    }, [hall.tags])

    const { value: purchasedPremiumPlan } = useIsPremiumPlanEnabled(hall.roomId)

    const courseVitrineEnabled = useMemo(() => {
      return tags.includes(COURSE_VITRINE_TAG)
    }, [tags])

    const watermarkDisabled = useMemo(() => {
      return tags.includes(DISABLE_WATERMARK_TAG)
    }, [tags])

    const issueEnabled = useMemo(() => {
      return tags.includes(ISSUE_TAG)
    }, [tags])

    const vodActivated = useMemo(() => {
      return tags.includes(VOD_ACTIVATED_TAG)
    }, [tags])

    const hideMemberCount = useMemo(() => {
      return tags.includes(HIDE_MEMBER_COUNT_TAG)
    }, [tags])

    const [protectContent, setProtectContent] = useState(hall.protectContent)

    const toggleProtectContent = useCallback(() => {
      if (!protectContent && !purchasedPremiumPlan) {
        analite('OPEN_BUY_PREMIUM_PLAN_FEATURE_DIALOG', {
          roomId: hall.roomId,
          source: 'HALL_SETTINGS',
          option: 'PROTECT_CONTENT',
        })
        showBuyPremiumFeatureDialog({
          featureType: PremiumFeatureType.PROTECT_CONTENT,
          hallId: hall.roomId,
        })
        return
      }
      setProtectContent(!protectContent)
    }, [hall, purchasedPremiumPlan, protectContent])

    const toggleTag = useCallback(
      (tag: string) => {
        const hasTag = tags.includes(tag)
        const roomCall = room?.call
        const callType = room?.call?.type
        const callStarted = isCallStarted(roomCall)
        const isTogglingDisableWatermarkTag = tag === DISABLE_WATERMARK_TAG

        if (
          !hasTag &&
          !purchasedPremiumPlan &&
          (tag === COURSE_VITRINE_TAG ||
            tag === ISSUE_TAG ||
            tag === HIDE_MEMBER_COUNT_TAG)
        ) {
          analite('OPEN_BUY_PREMIUM_PLAN_FEATURE_DIALOG', {
            roomId: hall.roomId,
            source: 'HALL_SETTINGS',
            option: tag,
          })
          showBuyPremiumFeatureDialog({
            featureType:
              tag === COURSE_VITRINE_TAG
                ? PremiumFeatureType.COURSE_VITRINE
                : tag === HIDE_MEMBER_COUNT_TAG
                  ? PremiumFeatureType.HIDE_MEMBER_COUNT
                  : PremiumFeatureType.QUESTION_AND_ANSWER,
            hallId: hall.roomId,
          })
          return
        }

        setTags(prevTags =>
          prevTags.includes(tag)
            ? removeTagFromHallTags(tag, prevTags || [])
            : addTagToHallTags(tag, tags || []),
        )

        if (
          callType === 'classroom' &&
          callStarted &&
          isTogglingDisableWatermarkTag
        )
          showChangeWatermarkSettingsConfirmationDialog()
      },
      [hall, tags, purchasedPremiumPlan, room?.call],
    )

    const isOfflineCoursePlanEnabled = useIsOfflineCoursePlanEnabled()

    const toggleVodActivation = useCallback(() => {
      if (!isOfflineCoursePlanEnabled && !purchasedPremiumPlan) {
        showBuyPremiumFeatureDialog({
          featureType: PremiumFeatureType.OFFLINE_COURSE,
          hallId: hall.roomId,
        })
      } else {
        toggleTag(VOD_ACTIVATED_TAG)
      }
    }, [isOfflineCoursePlanEnabled, toggleTag, purchasedPremiumPlan])

    const onOpenUserSessionsLimit = useCallback(() => {
      if (purchasedPremiumPlan) openUserSessionsLimit(hall.roomId)
      else {
        analite('OPEN_BUY_PREMIUM_PLAN_FEATURE_DIALOG', {
          roomId: hall.roomId,
          source: 'HALL_SETTINGS',
          option: 'USER_SESSIONS_LIMIT',
        })
        showBuyPremiumFeatureDialog({
          featureType: PremiumFeatureType.USER_SESSIONS_LIMIT,
          hallId: hall.roomId,
        })
      }
    }, [purchasedPremiumPlan, hall.roomId])

    useImperativeHandle(
      ref,
      () => ({
        getCourseVitrine: () => courseVitrineEnabled,
        getIssue: () => issueEnabled,
        getProtectContent: () => protectContent,
        getTags: () => tags,
        getVodActivated: () => vodActivated,
        getHideMemberCount: () => hideMemberCount,
        getWatermarkDisabled: () => watermarkDisabled,
      }),
      [
        courseVitrineEnabled,
        issueEnabled,
        protectContent,
        tags,
        vodActivated,
        hideMemberCount,
        watermarkDisabled,
      ],
    )
    const { value: userSessionsLimit } = useUserSessionsLimit(hall.roomId)

    return (
      <HallAdvancedSettingsOptions
        courseVitrineEnabled={courseVitrineEnabled}
        issueEnabled={issueEnabled}
        protectContent={protectContent}
        toggleTag={toggleTag}
        watermarkDisabled={watermarkDisabled}
        toggleProtectContent={toggleProtectContent}
        vodActivated={vodActivated}
        userSessionsLimit={userSessionsLimit}
        hideMemberCount={hideMemberCount}
        onOpenUserSessionsLimit={onOpenUserSessionsLimit}
        toggleVodActivation={toggleVodActivation}
      />
    )
  },
)
